/**
 * Layer API centralizzato di EdenFlow.
 *
 * PERCHÉ: storicamente le chiamate erano stringhe sparse in ogni pagina
 * (`api.get('/api/fatture')` → bug doppio prefisso, `data.scadenza` vs
 * `data_scadenza`, ecc.). Qui ogni endpoint è definito UNA volta sola.
 *
 * USO:
 *   import { Clienti, Task } from '../services/endpoints';
 *   const clienti = (await Clienti.list()).data;
 *   await Task.update(id, { stato: 'completata' });
 *
 * Tutte le funzioni ritornano la Promise di axios (usa `.data` per il payload).
 * Il prefisso '/api' è gestito dal baseURL in api.js — qui si scrive SENZA '/api'.
 */
import api from '../api';

export const Auth = {
  login: (params) => api.post('/auth/login', params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }),
  me: () => api.get('/auth/me'),
};

export const Clienti = {
  list: (params) => api.get('/clienti', { params }),
  attivi: () => api.get('/clienti', { params: { stato: 'attivo' } }),
  get: (id) => api.get(`/clienti/${id}`),
  update: (id, data) => api.put(`/clienti/${id}`, data),
};

export const Task = {
  list: (params) => api.get('/task', { params }),
  byCliente: (clienteId) => api.get('/task', { params: { cliente_id: clienteId } }),
  create: (data) => api.post('/task', data),
  update: (id, data) => api.put(`/task/${id}`, data),
  remove: (id) => api.delete(`/task/${id}`),
};

export const Scadenzario = {
  list: ({ giorni = 30, stato } = {}) => api.get('/scadenzario', { params: { giorni, ...(stato ? { stato } : {}) } }),
};

export const Ped = {
  list: (clienteId, orizzonte) => api.get('/ped', { params: { cliente_id: clienteId, orizzonte } }),
  stats: (clienteId, orizzonte = 'corrente') => api.get(`/ped/stats/${clienteId}`, { params: { orizzonte } }),
  create: (data) => api.post('/ped', data),
  update: (id, data) => api.put(`/ped/${id}`, data),
  approva: (id, approvato) => api.patch(`/ped/${id}/approva`, { approvato }),
  remove: (id) => api.delete(`/ped/${id}`),
};

export const ClienteSezioni = {
  list: (clienteId) => api.get(`/cliente_sezioni/${clienteId}`),
  save: (clienteId, sezione, contenuto) => api.put(`/cliente_sezioni/${clienteId}/${sezione}`, { contenuto }),
  history: (clienteId) => api.get(`/cliente_sezioni/${clienteId}/history`),
};

export const Assets = {
  list: (clienteId) => api.get(`/cliente_assets/${clienteId}`),
  add: (clienteId, data) => api.post(`/cliente_assets/${clienteId}`, data),
  remove: (assetId) => api.delete(`/cliente_assets/${assetId}`),
};

export const BrandChecklist = {
  get: (clienteId) => api.get(`/cliente_brand_checklist/${clienteId}`),
  save: (clienteId, state) => api.put(`/cliente_brand_checklist/${clienteId}`, { state }),
};

export const Fatture = {
  list: () => api.get('/fatture'),
  stats: () => api.get('/fatture/stats'),
  create: (data) => api.post('/fatture', data),
  update: (id, data) => api.put(`/fatture/${id}`, data),
  remove: (id) => api.delete(`/fatture/${id}`),
};

export const FE = {
  emittente: () => api.get('/fe/emittente'),
  saveEmittente: (data) => api.put('/fe/emittente', data),
  list: (params) => api.get('/fe', { params }),
  stats: () => api.get('/fe/stats'),
  get: (id) => api.get(`/fe/${id}`),
  xml: (id) => api.get(`/fe/${id}/xml`, { responseType: 'text' }),
  create: (data) => api.post('/fe', data),
  rigenera: (id) => api.post(`/fe/${id}/rigenera`),
  invia: (id) => api.post(`/fe/${id}/invia`, { conferma: true }),
  aggiornaStato: (id) => api.post(`/fe/${id}/aggiorna-stato`),
  verificaServizio: () => api.post('/fe/verifica-servizio'),
  remove: (id) => api.delete(`/fe/${id}`),
};

export const Analytics = {
  dashboard: (params) => api.get('/meta_analytics/dashboard', { params }),
  agenzia: () => api.get('/meta_analytics/agenzia'),
  posts: (params) => api.get('/meta_analytics/posts', { params }),
  compare: (params) => api.get('/meta_analytics/compare', { params }),
  goals: (clienteId) => api.get(`/analytics_goals/${clienteId}`),
  saveGoal: (data) => api.post('/analytics_goals', data),
};

export const Workload = {
  team: () => api.get('/workload'),
  userTasks: (userId) => api.get(`/workload/${userId}/tasks`),
};

export const Home = {
  stats: () => api.get('/home/stats'),
};

export const Upload = {
  file: (formData) => api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (filename) => api.delete(`/upload/${filename}`),
};

export default {
  Auth, Clienti, Task, Scadenzario, Ped, ClienteSezioni,
  Assets, BrandChecklist, Fatture, FE, Analytics, Workload, Home, Upload,
};
